import axios from 'axios'
import { parseFieldStr, serializeFieldStr } from './fieldutils.mjs'
import debug from '../debug'

const listIndex = /\.(\d+)/g

function StandardInstanceId(instanceId, generic) {
  return (instanceId && typeof instanceId === 'string')
    ? instanceId.replace(listIndex, generic ? '[]' : '[$1]')
    : instanceId
}

function GetObjectKeyValue (pojo, varOrQueryDef, typeDict) {
  if (Array.isArray(pojo)) return // or throw new Error('Argument exception: Array not allowed')
  // we most have only a single item to find its single key
  if (pojo && typeof pojo === 'object') {
    const keyName = GetVarKeyName(varOrQueryDef, typeDict)
    if (keyName) {
      const keyValue = pojo[keyName]
      if (keyValue && typeof keyValue === 'object') { // key is (itself) selection... recurse to get primitive value
        const model = typeDict[varOrQueryDef.typeName]
        const keyVar = model.varDict[keyName]
        if (keyVar) {
          return GetObjectKeyValue(keyValue, keyVar, typeDict)
        } else {
          console.error(`Invalid/unexpected data: cannot get child key for ${varOrQueryDef.name}/${keyName}`)
          return // undefined
        }
      } // else
      return keyValue
    }
  }
}

function GetVarKeyName (varOrQueryDef, typeDict) {
  if (varOrQueryDef.type === 'selection' && varOrQueryDef.optionKeyName) {
    return varOrQueryDef.optionKeyName
  }
  if (varOrQueryDef.provider && varOrQueryDef.source && varOrQueryDef.keyName) {
    return varOrQueryDef.keyName
  }
  if (varOrQueryDef.typeName) {
    const modelDef = typeDict[varOrQueryDef.typeName]
    if (modelDef) {
      // remember to check for intentionally KEYLESS objects... those where the key type is none!
      return modelDef.key || (modelDef.keyType === 'none') ? undefined : 'id$'
    }
  }
}

function GetConfirmedKeyVal (itemId, queryDef, parentContext) {
  // recent change to support object keys of type "selection" means, itemId may now be an object instead of a primitive.
  // if so, drill down to get the appropriate primitive key value!
  if (itemId && typeof itemId === 'object') {
    // does not work if/when child object is not yet reflected in the current object state:
    // let currContext = parentContext.getInstance(instanceId)
    // let keyPath = currContext.getKeyPath(true)
    // itemId = _.get(itemId, keyPath)
    // better/safer approach?:
    if (queryDef.typeName) {
      const modelDef = parentContext.typeDefs[queryDef.typeName]
      const keyName = modelDef.key || queryDef.keyName
      const varDef = modelDef.variables.find(v => v.name === keyName)
      itemId = GetObjectKeyValue(itemId, varDef, parentContext.typeDefs)
    } else {
      throw new Error('Query has no typeName')
    }
  }
  return itemId
}

// The idea here is to consolidate the code used to retrieve data from knackly server-side queries
// (regardless of provider).  It does stuff like...
// - retrieve all records from a query
// - keyword searching through all query records
// - retrieve one selected record in its entirety
// - requerying/refreshing specific records that had already been selected in the past
// Looking at this list, I start thinking, maybe we should figure out how to use react-query!?

export async function FetchItem (queryDef, itemId, keyName, parentContext, instanceId, bAlwaysReturn, dnrFields) {
  const keyVal = GetConfirmedKeyVal(itemId, queryDef, parentContext)
  // problem: for a Clio matter query with a lot of mapping to connect related contacts,
  // queryDef.fields is defined BUT it does not refer to any contacts for some reason, so fetchItem
  // does not return a fraction of what is needed.  '&& !queryDef.mapTo' hack below to avoid that?!
  // ALSO: callers don't always know what fields to request, but sometimes, they know what fields NOT to request.
  // That's what dnrFields is for. Apologies that this is a mess. Needs improvement!
  let requestFields = '', allFields = ''
  const genericInst = StandardInstanceId(instanceId, true)
  const instanceFields = queryDef.fields
  if (genericInst && instanceFields && (genericInst in instanceFields) && !queryDef.mapTo) {
    requestFields = instanceFields[genericInst]
  } else {
    allFields = parentContext.getVarNamesAll(queryDef.typeName, false)
    if (queryDef.provider !== 'Knackly') {
      requestFields = allFields
    }
  }
  if (requestFields && dnrFields) { // limit the given fields to key-value-only -- don't request any child fields
    const fieldObj = ApplyDNRFields(parseFieldStr(requestFields), dnrFields)
    requestFields = serializeFieldStr(fieldObj)
  }
  const itemUrl = `/api/queries/${queryDef.name}/${keyVal}`
  const options = {
    headers: { 'knackly-appkey': parentContext.global.appKey },
  }
  if (requestFields) {
    options.params = { f: requestFields }
  }
  const response = await axios.get(itemUrl, options)
  // compare to already-cached value, return new value if there are differences (or if bAlwaysReturn == true)
  return UpdateCachedValue(parentContext, queryDef.typeName, keyName, keyVal, response.data,
    requestFields || allFields, bAlwaysReturn)
}

export async function SaveItem (queryDef, keyName, parentContext, instanceId, newValue, oldValue) {
  const itemId = GetConfirmedKeyVal(newValue[keyName], queryDef, parentContext)
  const saveValue = oldValue
    ? { ...newValue, __origValue: oldValue }
    : newValue
  const allFields = (queryDef.fields && (instanceId in queryDef.fields) && !queryDef.mapTo)
    ? queryDef.fields[instanceId]
    : parentContext.getVarNamesAll(queryDef.typeName, queryDef.provider === 'Knackly')
  const saveUrl = `/api/queries/${queryDef.name}/save/${itemId}`
  const response = await axios.post(saveUrl, saveValue, {
      params: { f: allFields },
      headers: { 'knackly-appkey': parentContext.global.appKey },
  })
  return UpdateCachedValue(parentContext, queryDef.typeName, keyName, itemId, response.data, allFields, false)
}

export async function CreateItem (queryDef, keyName, parentContext, instanceId, saveValue = undefined) {
  const allFields = (queryDef.fields && (instanceId in queryDef.fields) && !queryDef.mapTo)
    ? queryDef.fields[instanceId]
    : parentContext.getVarNamesAll(queryDef.typeName, queryDef.provider === 'Knackly')
  const saveUrl = `/api/queries/${queryDef.name}/save`
  const response = await axios.post(saveUrl, saveValue, {
      params: { f: allFields },
      headers: { 'knackly-appkey': parentContext.global.appKey },
  })
  const itemId = GetConfirmedKeyVal(response.data[keyName], queryDef, parentContext)
  return UpdateCachedValue(parentContext, queryDef.typeName, keyName, itemId, response.data, allFields, true)
}

function UpdateCachedValue (context, typeName, keyName, itemId, value, fields, bAlwaysReturn) {
  if (value) {
    if (!(keyName in value)) {
      value[keyName] = itemId
    } else {
      const keyVal = value[keyName]
      if (typeof keyVal !== 'object') {
        debug.assert(keyVal === itemId, 'record ID mismatch')
      }
    }
    const fieldObj = typeof fields === 'object' ? fields : parseFieldStr(fields)
    const newValue = context.getDataMgr().updateValue(typeName, fieldObj, value)
    if ('__keyVal__' in newValue) {
      delete newValue.__keyVal__
    }
    if (newValue.__mutated__) {
      delete newValue.__mutated__
      return newValue
    } else {
      return bAlwaysReturn ? newValue : undefined
    }
  }
  return value
}

export async function FetchOptions (queryDef, optFields, args, parentContext, skip, search) {
  if (!CheckReqParams(queryDef, args)) {
    return
  }
  const searchParams = new URLSearchParams()
  if (optFields) {
    searchParams.append('f', optFields)
  }
  if (search) {
    searchParams.append('s', search)
  }
  if (skip) {
    searchParams.append('skip', skip)
  }
  Object.entries(args).forEach(([argName, argValue]) => {
    searchParams.append(argName, argValue)
  })
  const qstr = searchParams.toString()
  const url = `/api/queries/${queryDef.name}?${qstr}`
  const response = await axios.get(url, {
    headers: { 'knackly-appkey': parentContext.global.appKey }
  })
  return response.data
}

export function CheckReqParams (queryDef, args) {
  if (!queryDef.params) {
    return true
  }
  const reqParams = queryDef.params.filter(param => param.req).map(param => param.name)
  if (reqParams.length) {
    if (!args) {
      return false
    }
    reqParams.forEach(param => {
      if (!(param in args)) {
        return false
      }
    })
    return true
  }
  return true
}

function ApplyDNRFields (fieldObj, limitFields) {
  if (fieldObj && typeof fieldObj === 'object') {
    if (limitFields && typeof limitFields === 'object') {
      for (const [keyName, keyVal] of Object.entries(limitFields)) {
        if (keyName in fieldObj) {
          fieldObj[keyName] = ApplyDNRFields(fieldObj[keyName], keyVal)
        }
      }
    } else {
      fieldObj = limitFields
    }
  }
  return fieldObj
}
